import { FC, useCallback } from "react";

import { serverGetSectionScoreReport } from "api/api-server-assessment";
import { SectionScoreTableRow } from "api/api-types-assessment";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";

type SectionScoreTableProps = {
  activitiesMaxValue: number;
  assignmentsMaxValue: number;
  prepWorkMaxValue: number;
  sectionId: string;
  sectionScoreTableRows: SectionScoreTableRow[] | null;
};

export const SectionScoreTable: FC<SectionScoreTableProps> = ({
  activitiesMaxValue,
  assignmentsMaxValue,
  prepWorkMaxValue,
  sectionId,
  sectionScoreTableRows,
}) => {
  const exportData = useCallback(() => {
    return serverGetSectionScoreReport({ section_id: sectionId });
  }, [sectionId]);

  if (!sectionScoreTableRows || sectionScoreTableRows.length === 0) {
    return null;
  }

  return (
    <ScrollableTableContainer
      exportData={exportData}
      title={t("assessment_dashboard.student_section_scores.title")}
    >
      <Table>
        {/* Headers: Student / Prep Work / In-Class Work / Assignments / Total Section Score */}
        <THead>
          <TR className="border-black-tint-70">
            <TH className="w-16 pl-4">{t("assessment_dashboard.all.student_name_header_title")}</TH>

            {!!prepWorkMaxValue && (
              <TH className="w-16">{t("assessment_dashboard.grades.prepwork")}</TH>
            )}

            {!!activitiesMaxValue && (
              <TH className="w-16">{t("assessment_dashboard.grades.activities")}</TH>
            )}

            {!!assignmentsMaxValue && (
              <TH className="w-16">{t("assessment_dashboard.grades.assignments")}</TH>
            )}

            <TH className="w-16">
              <Tooltip text={t("assessment_dashboard.tooltip.section_score")}>
                {t("assessment_dashboard.student_section_scores.total_section_score_header_title")}
              </Tooltip>
            </TH>
          </TR>
        </THead>

        <tbody className="font-normal">
          {sectionScoreTableRows.map((row) => (
            <TR key={row.student_id}>
              <TD className="max-w-40 truncate py-2.5 pl-[17px]" title={row.student_name}>
                {row.student_name}
              </TD>

              {!!prepWorkMaxValue && <TD>{row.prep_work_percent_score}</TD>}

              {!!activitiesMaxValue && <TD>{row.activities_percent_score}</TD>}

              {!!assignmentsMaxValue && <TD>{row.assignments_percent_score}</TD>}

              <TD>{row.total_section_score}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
